import pdfMake from 'pdfmake/build/pdfmake'
import { carregarImagem, clarear } from './gerar-pdf-dashboard'

export interface DestaqueDoPdf {
	rotulo: string
	valor: string
	cor: string
	observacao?: string
}

export interface KpiDoPdf {
	titulo: string
	valor: string
	subtitulo?: string
}

export interface GraficoDoPdf {
	titulo: string
	subtitulo: string
	destaques: DestaqueDoPdf[]
	observacao?: string
	imagem: string
}

export interface MetaDoPdf {
	percentual: number
	cor: string
	situacao: string
	acimaDaMeta: number
	meta: string
	realizado: string
	falta: string
	diasRestantes: string
	projecao: string
	corProjecao: string
	percentualProjecao: number
}

export interface DashboardDeAcaoPdfParams {
	corPrimaria: string
	nome: string
	descricao: string
	periodo: string
	statusLabel: string
	statusColor: string
	efeitoTitulo: string
	efeitos: DestaqueDoPdf[]
	kpis: KpiDoPdf[]
	graficos: GraficoDoPdf[]
	meta?: MetaDoPdf
	geradoEm: string
	tipo: 'dobro' | 'data'
	statusBg: string
}

const TIPOS_DE_RELATORIO = {
	dobro: { titulo: 'Dobro de cashback', cor: '#E69138', prefixoDoArquivo: 'dobro' },
	data: { titulo: 'Reativação de cashback', cor: '#2A78D6', prefixoDoArquivo: 'reativacao' },
}

function barraDeTitulo(cor: string, altura: number) {
	return { width: 10, canvas: [{ type: 'rect', x: 0, y: 1, w: 3, h: altura, color: cor }] }
}

function etiquetaColorida(texto: string, corDeFundo: string, corDoTexto: string, tamanho = 8) {
	return {
		table: { widths: ['auto'], body: [[{ text: texto, fontSize: tamanho, bold: true, color: corDoTexto, margin: [8, 3, 8, 3] }]] },
		layout: { fillColor: () => corDeFundo, hLineWidth: () => 0, vLineWidth: () => 0 },
	}
}

function setaDoEfeito(cor: string) {
	return {
		width: 34,
		margin: [0, 16, 0, 0],
		canvas: [
			{ type: 'line', x1: 4, y1: 10, x2: 26, y2: 10, lineWidth: 2.5, lineColor: cor },
			{ type: 'polyline', points: [{ x: 19, y: 3 }, { x: 27, y: 10 }, { x: 19, y: 17 }], lineWidth: 2.5, lineColor: cor },
		],
	}
}

export async function svgParaImagem(svg: SVGSVGElement, larguraEmPixels = 1600): Promise<string> {
	const [, , largura, altura] = (svg.getAttribute('viewBox') || '0 0 820 240').split(' ').map(Number)
	const copia = svg.cloneNode(true) as SVGSVGElement
	copia.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
	copia.setAttribute('width', String(largura))
	copia.setAttribute('height', String(altura))
	copia.setAttribute('font-family', 'Roboto, Arial, sans-serif')
	copia.removeAttribute('style')
	copia.removeAttribute('class')

	const endereco = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copia)], { type: 'image/svg+xml;charset=utf-8' }))
	try {
		const imagem = await new Promise<HTMLImageElement>((resolve, reject) => {
			const elemento = new Image()
			elemento.onload = () => resolve(elemento)
			elemento.onerror = () => reject(new Error('Não foi possível renderizar o gráfico para o PDF'))
			elemento.src = endereco
		})
		const canvas = document.createElement('canvas')
		canvas.width = larguraEmPixels
		canvas.height = Math.round(altura * (larguraEmPixels / largura))
		const contexto = canvas.getContext('2d') as CanvasRenderingContext2D
		contexto.fillStyle = '#ffffff'
		contexto.fillRect(0, 0, canvas.width, canvas.height)
		contexto.drawImage(imagem, 0, 0, canvas.width, canvas.height)
		return canvas.toDataURL('image/png')
	} finally {
		URL.revokeObjectURL(endereco)
	}
}

function cartaoDeKpi(kpi: KpiDoPdf, cor: string, fundo: string) {
	const conteudo: any[] = [
		{ text: kpi.titulo.toUpperCase(), fontSize: 7, color: '#8A8A8A', characterSpacing: 0.5 },
		{ text: kpi.valor, fontSize: 15, bold: true, color: '#2B2B2F', margin: [0, 3, 0, 0] },
		{ text: kpi.subtitulo || ' ', fontSize: 8, color: '#9A9AA2', margin: [0, 3, 0, 0] },
	]

	return {
		table: { widths: ['*'], body: [[{ margin: [10, 9, 10, 9], stack: conteudo }]] },
		layout: {
			fillColor: () => fundo,
			hLineWidth: (i: number) => (i === 0 ? 3 : 0),
			vLineWidth: () => 0,
			hLineColor: () => cor,
			paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
		},
	}
}

function caixaDeDestaque(destaque: DestaqueDoPdf) {
	const conteudo: any[] = [
		{ text: destaque.rotulo, fontSize: 8, color: '#8A8A92' },
		{ text: destaque.valor, fontSize: 17, bold: true, color: destaque.cor, margin: [0, 3, 0, 0] },
	]
	if (destaque.observacao) conteudo.push({ text: destaque.observacao, fontSize: 8, color: destaque.cor, margin: [0, 3, 0, 0] })

	return {
		table: { widths: ['*'], body: [[{ margin: [12, 10, 12, 10], stack: conteudo }]] },
		layout: {
			fillColor: () => '#FAFAFB',
			hLineWidth: () => 0.5,
			vLineWidth: () => 0.5,
			hLineColor: () => '#E6E4EC',
			vLineColor: () => '#E6E4EC',
		},
	}
}

function blocoDeMeta(meta: MetaDoPdf, corDoTipo: string) {
	const larguraDaBarra = 786
	const larguraPreenchida = larguraDaBarra * Math.min(Math.max(meta.percentual, 0), 100) / 100
	const barra: any[] = [{ type: 'rect', x: 0, y: 0, w: larguraDaBarra, h: 10, r: 5, color: '#EEEEF2' }]
	if (larguraPreenchida > 0) barra.push({ type: 'rect', x: 0, y: 0, w: larguraPreenchida, h: 10, r: 5, color: meta.cor })

	const destaques: DestaqueDoPdf[] = [
		{ rotulo: 'Meta definida', valor: meta.meta, cor: '#2B2B2F', observacao: 'valor manual' },
		{ rotulo: 'Realizado', valor: meta.realizado, cor: '#1E7D46', observacao: 'vendas acumuladas' },
		{ rotulo: 'Falta para a meta', valor: meta.falta, cor: '#2B2B2F', observacao: meta.diasRestantes },
		{ rotulo: 'Projeção no fim da ação', valor: meta.projecao, cor: meta.corProjecao, observacao: `${meta.percentualProjecao}% da meta` },
	]

	return {
		unbreakable: true,
		margin: [0, 14, 0, 0],
		stack: [
			{
				columns: [
					{
						width: '*',
						columns: [
							barraDeTitulo(corDoTipo, 24),
							{
								width: '*',
								stack: [
									{ text: 'Meta de vendas da ação', fontSize: 12, bold: true, color: '#2B2B2F' },
									{ text: 'Meta manual definida em valor de vendas', fontSize: 8, color: '#9A9AA2', margin: [0, 2, 0, 0] },
								],
							},
						],
					},
					{ width: 'auto', text: meta.situacao, fontSize: 10, bold: true, color: meta.cor, alignment: 'right' },
				],
				margin: [0, 0, 0, 8],
			},
			{
				columns: [
					{
						width: 'auto',
						text: [
							{ text: `${meta.percentual}%`, fontSize: 26, bold: true, color: meta.cor },
							...(meta.acimaDaMeta > 0 ? [{ text: `   +${meta.acimaDaMeta}% acima da meta`, fontSize: 9, bold: true, color: '#1E7D46' }] : []),
						],
					},
					{ width: '*', text: `${meta.realizado} de ${meta.meta}`, fontSize: 9, color: '#9A9AA2', alignment: 'right', margin: [0, 14, 0, 0] },
				],
				margin: [0, 0, 0, 6],
			},
			{ canvas: barra, margin: [0, 0, 0, 10] },
			{ columns: destaques.map(caixaDeDestaque), columnGap: 10 },
		],
	}
}

function blocoDeGrafico(grafico: GraficoDoPdf, cor: string) {
	const cabecalho: any[] = [
		{
			width: '*',
			columns: [
				barraDeTitulo(cor, 24),
				{
					width: '*',
					stack: [
						{ text: grafico.titulo, fontSize: 12, bold: true, color: '#2B2B2F' },
						{ text: grafico.subtitulo, fontSize: 8, color: '#9A9AA2', margin: [0, 2, 0, 0] },
					],
				},
			],
		},
		{
			width: 'auto',
			columns: grafico.destaques.map(destaque => ({
				width: 'auto',
				stack: [
					{ text: destaque.rotulo, fontSize: 7, color: '#6A6A72', alignment: 'right' },
					{ text: destaque.valor, fontSize: 12, bold: true, color: destaque.cor, alignment: 'right' },
				],
			})),
			columnGap: 16,
		},
	]

	const conteudo: any[] = [{ columns: cabecalho, margin: [0, 0, 0, 6] }]
	if (grafico.observacao) conteudo.push({ text: grafico.observacao, fontSize: 8, color: cor, margin: [0, 0, 0, 4] })
	conteudo.push({ image: grafico.imagem, width: 700, alignment: 'center' })

	return { unbreakable: true, margin: [0, 14, 0, 0], stack: conteudo }
}

export async function gerarPdfDashboardDeAcaoDeCashback(params: DashboardDeAcaoPdfParams) {
	const cor = params.corPrimaria || '#60269E'
	const tipoDeRelatorio = TIPOS_DE_RELATORIO[params.tipo]
	const corDoTipo = tipoDeRelatorio.cor
	const fundoKpi = clarear(corDoTipo)
	const logo = await carregarImagem('/logo-almode.png')

	const marca = logo
		? { image: logo, width: 86 }
		: { text: 'ALMODE', color: 'white', bold: true, fontSize: 16, characterSpacing: 3, margin: [0, 8, 0, 0] }

	const conteudo: any[] = [
		{
			table: {
				widths: ['auto', '*'],
				body: [[
					{ stack: [marca], fillColor: cor, margin: [14, 12, 6, 12], border: [false, false, false, false] },
					{
						fillColor: cor, margin: [6, 25, 14, 10], border: [false, false, false, false], alignment: 'left',
						stack: [
							{ text: tipoDeRelatorio.titulo, color: 'white', bold: true, fontSize: 18 },
							{ text: 'Relatório consolidado da ação', color: 'white', fontSize: 10, margin: [0, 2, 0, 0], opacity: 0.9 },
						],
					},
				]],
			},
			layout: 'noBorders',
			margin: [0, 0, 0, 12],
		},
		{
			table: {
				widths: ['*'],
				body: [[
					{
						fillColor: fundoKpi, margin: [12, 10, 12, 10],
						columns: [
							{
								width: '*',
								stack: [
									{ text: params.nome, fontSize: 15, bold: true, color: '#2B2B2F' },
									{ text: params.descricao, fontSize: 9, color: '#8A8A92', margin: [0, 2, 0, 0] },
								],
							},
							{
								width: 'auto',
								alignment: 'right',
								stack: [
									{
										columns: [
											{ width: '*', text: '' },
											{ width: 'auto', ...etiquetaColorida(params.statusLabel, params.statusBg, params.statusColor, 9) },
										],
									},
									{ text: params.periodo, fontSize: 9, color: '#6A6A72', margin: [0, 5, 0, 0] },
								],
							},
						],
					},
				]],
			},
			layout: 'noBorders',
			margin: [0, 0, 0, 14],
		},
		{
			columns: [
				barraDeTitulo(corDoTipo, 11),
				{ width: '*', text: params.efeitoTitulo.toUpperCase(), fontSize: 9, bold: true, color: '#8A8A92', characterSpacing: 0.4 },
			],
			margin: [0, 0, 0, 6],
		},
		{
			columns: params.efeitos.length === 2
				? [caixaDeDestaque(params.efeitos[0]), setaDoEfeito(corDoTipo), caixaDeDestaque(params.efeitos[1])]
				: params.efeitos.map(caixaDeDestaque),
			columnGap: params.efeitos.length === 2 ? 0 : 14,
			margin: [0, 0, 0, 14],
		},
		{ columns: params.kpis.map(kpi => cartaoDeKpi(kpi, corDoTipo, fundoKpi)), columnGap: 8 },
	]

	params.graficos.forEach((grafico, indice) => {
		conteudo.push(blocoDeGrafico(grafico, corDoTipo))
		if (indice === 0 && params.meta) conteudo.push(blocoDeMeta(params.meta, corDoTipo))
	})

	const documento: any = {
		pageSize: 'A4',
		pageOrientation: 'landscape',
		pageMargins: [28, 28, 28, 38],
		content: conteudo,
		defaultStyle: { fontSize: 9, color: '#333' },
		footer: (paginaAtual: number, totalDePaginas: number) => ({
			columns: [
				{ width: '*', text: `${tipoDeRelatorio.titulo} · ${params.nome}`, fontSize: 8, color: '#9A9A9A', margin: [28, 0, 0, 0] },
				{ width: 'auto', text: `Gerado pelo Almode em ${params.geradoEm}   ${paginaAtual} / ${totalDePaginas}`, alignment: 'right', fontSize: 8, color: '#9A9A9A', margin: [0, 0, 28, 0] },
			],
			margin: [0, 12, 0, 0],
		}),
	}

	const nomeDoArquivo = params.nome
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/[^a-zA-Z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.toLowerCase()

	pdfMake.createPdf(documento).download(`dashboard-${tipoDeRelatorio.prefixoDoArquivo}-${nomeDoArquivo || 'acao-de-cashback'}.pdf`)
}
